import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { Confirmation } from '@abp/ng.theme.shared';
import { ConfirmationService } from 'src/core/services/confirmation/confirmation.service';
import { of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { ListResponseDto } from 'src/core/models/request/list-response.dto';
import { CrudService } from 'src/core/services/crud/crud.service';
import { ToasterService } from 'src/core/services/toaster/toaster.service';
import { FreeSwitchInstanceDto } from '../../models/free-switch-instance.dto';
import { FreeSwitchIntegrationFormComponent } from '../freeswitch-integration-form/freeswitch-integration-form.component';
import { FreeSwitchIntegrationListComponent } from '../freeswitch-integration-list/freeswitch-integration-list.component';
import { FreeSwitchIntegrationToolbarComponent } from '../freeswitch-integration-toolbar/freeswitch-integration-toolbar.component';
import { FreeSwitchInstanceConsoleComponent } from '../freeswitch-instance-console/freeswitch-instance-console.component';

@Component({
  selector: 'app-freeswitch-integration-main',
  standalone: true,
  imports: [
    CommonModule,
    FreeSwitchIntegrationToolbarComponent,
    FreeSwitchIntegrationListComponent,
    FreeSwitchIntegrationFormComponent,
    FreeSwitchInstanceConsoleComponent,
  ],
  templateUrl: './freeswitch-integration-main.component.html',
  styleUrls: ['./freeswitch-integration-main.component.scss'],
})
export class FreeSwitchIntegrationMainComponent implements OnInit {
  private readonly crudService = inject(CrudService);
  private readonly toaster = inject(ToasterService);
  private readonly confirmationService = inject(ConfirmationService);

  instances: FreeSwitchInstanceDto[] = [];
  loading = true;
  showFormModal = false;
  selectedInstance: FreeSwitchInstanceDto | null = null;
  isEditMode = false;

  showConsoleModal = false;
  consoleInstance: FreeSwitchInstanceDto | null = null;

  ngOnInit(): void {
    this.loadList();
  }

  get canDelete(): boolean {
    return this.instances.length > 1;
  }

  loadList(): void {
    this.loading = true;

    this.crudService.get<FreeSwitchInstanceDto>(FreeSwitchInstanceDto, null).subscribe({
      next: (response: ListResponseDto<FreeSwitchInstanceDto>) => {
        this.instances = response.items ?? [];
        this.loading = false;
      },
      error: () => {
        this.loading = false;
      },
    });
  }

  openAddModal(): void {
    this.selectedInstance = null;
    this.isEditMode = false;
    this.showFormModal = true;
  }

  openEditModal(instance: FreeSwitchInstanceDto): void {
    this.selectedInstance = instance;
    this.isEditMode = true;
    this.showFormModal = true;
  }

  onFormVisibilityChange(visible: boolean): void {
    this.showFormModal = visible;

    if (!visible) {
      this.selectedInstance = null;
    }
  }

  onFormSaved(isCreate: boolean): void {
    this.showFormModal = false;
    this.selectedInstance = null;
    this.toaster.success(
      isCreate
        ? 'FreeswitchIntegration::InstanceSuccessfullyCreated'
        : 'FreeswitchIntegration::InstanceSuccessfullyUpdated',
      '',
      null,
    );
    this.loadList();
  }

  openConsoleModal(instance: FreeSwitchInstanceDto): void {
    this.consoleInstance = instance;
    this.showConsoleModal = true;
  }

  onConsoleVisibilityChange(visible: boolean): void {
    this.showConsoleModal = visible;

    if (!visible) {
      this.consoleInstance = null;
    }
  }

  openDeleteModal(instance: FreeSwitchInstanceDto): void {
    this.confirmationService
      .warn('FreeswitchIntegration::AreYouSureDelete', 'FreeswitchIntegration::ConfirmDelete', {
        hideCancelBtn: false,
        hideYesBtn: false,
        dismissible: true,
        yesText: 'FreeswitchIntegration::Delete',
        cancelText: 'FreeswitchIntegration::Cancel',
      })
      .pipe(
        switchMap(status => {
          if (status === Confirmation.Status.confirm) {
            return this.crudService
              .delete(FreeSwitchInstanceDto, instance.id)
              .pipe(map(() => true));
          }

          return of(false);
        }),
      )
      .subscribe({
        next: done => {
          if (done) {
            this.toaster.success('FreeswitchIntegration::InstanceSuccessfullyDeleted', '', null);
            this.loadList();
          }
        },
      });
  }
}
